import { Box } from "@mui/material";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { StyledPageWrapper } from "Components/Shared/StyledComponents";
import { useNavigate } from "Navigation/useNavigate";
import { appUrl } from "Utils/UrlUtils";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { PhoneInput } from "Components/Shared/Inputs/Form/PhoneInput";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { FormSection } from "Components/Shared/FormSection";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { getPhoneSchema } from "Schemas/phoneSchema";
import { NO_VAT_NUMBER } from "Schemas/vatNumberSchema";
import { Gender, RelationType } from "Infrastructure/Api/Api";
import { toast } from "sonner";
import { useCreateEmployeeMutation } from "Api/Mutations/Merchants/Employees/useCreateEmployeeMutation";
import { useCreatePartyWithCurrentUserRelation } from "Hooks/useCreatePartyWithCurrentUserRelation";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";
import { Resources, useResource, type TFunction } from "Translations/Resources";
import { EMPTY_PHONE } from "Utils/PhoneUtils";
import { useGetMerchantQuery } from "Api/Queries/Merchants/useGetMerchantQuery";

const createEmployeeSchema = (t: TFunction) =>
  z.object({
    firstName: z.string().min(1, t(Resources.Validation.Required)),
    lastName: z.string().min(1, t(Resources.Validation.Required)),
    email: z.email(t(Resources.Validation.InvalidEmail)),
    phone: getPhoneSchema(t),
  });

type CreateEmployeeFormData = z.infer<ReturnType<typeof createEmployeeSchema>>;

export const CreateEmployeePage = () => {
  const navigate = useNavigate();
  const { t } = useResource();
  const { control, handleSubmit } = useForm<CreateEmployeeFormData>({
    resolver: zodResolver(createEmployeeSchema(t)),
    defaultValues: { phone: EMPTY_PHONE },
  });

  const { data: currentMerchant, isLoading: isLoadingCurrentMerchant } =
    useGetMerchantQuery();

  const { mutate: createEmployee, isPending: isCreatingEmployee } =
    useCreateEmployeeMutation({
      onSuccess: ({ publicID }) => {
        navigate(`${appUrl("employee-detail")}/${publicID}`);
        toast.success(t(Resources.Employee.Create.CreateSuccess));
      },
    });

  const { postPartyWithCurrentUserRelation, isPending: isCreatingParty } =
    useCreatePartyWithCurrentUserRelation({
      relationType: RelationType.MerchantsEmployee,
      onSuccess: (publicPartyID) => {
        const merchantPublicID = currentMerchant?.publicID;

        if (!merchantPublicID) {
          toast.error(t(Resources.Employee.Create.MissingMerchantID));
          return;
        }

        createEmployee({ data: { publicPartyID }, merchantPublicID });
      },
      onError: () => {
        toast.error(t(Resources.Employee.Create.CreateError));
      },
    });

  if (isLoadingCurrentMerchant) {
    return <CenteredCircularProgress />;
  }

  const isPending = isCreatingParty || isCreatingEmployee;

  const onSubmit = (data: CreateEmployeeFormData) => {
    postPartyWithCurrentUserRelation({
      ...data,
      gender: Gender.NotSet,
      vatNumber: NO_VAT_NUMBER, // an employee is a natural person
      otherNationalities: [],
    });
  };

  return (
    <AuthenticatedLayout
      title={t(Resources.Employee.Create.Title)}
      backNavigation={{
        mode: "historyStack",
        fallbackUrl: appUrl("employees"),
      }}
    >
      <StyledPageWrapper>
        <PrimaryPaper>
          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <FormSection legend={t(Resources.Employee.Create.Legend)}>
              <FormInput
                label={t(Resources.Common.FirstName)}
                name="firstName"
                control={control}
                fullWidth
              />
              <FormInput
                label={t(Resources.Common.LastName)}
                name="lastName"
                control={control}
                fullWidth
              />
              <PhoneInput
                label={t(Resources.Common.Phone)}
                name="phone"
                control={control}
              />
              <FormInput
                label={t(Resources.Common.Email)}
                name="email"
                type="email"
                control={control}
                fullWidth
                valueTransformer={(v) => v.toLowerCase()}
              />
              <PrimaryButton
                fullWidth
                type="submit"
                isLoading={isPending}
                variant="contained"
                color="primary"
              >
                {t(Resources.Employee.Create.CreateButton)}
              </PrimaryButton>
            </FormSection>
          </Box>
        </PrimaryPaper>
      </StyledPageWrapper>
    </AuthenticatedLayout>
  );
};

export default CreateEmployeePage;
